/**
 * OneSignal Web SDK loader + login/logout helpers.
 *
 * Shared between Core (apps/core) and Fitting (apps/fitting) so both
 * apps' service workers + push subscriptions hang off the same
 * external_user_id = auth.uid mapping. The server-side edge functions
 * (send-message-notification, send-listing-message-notification,
 * send-alert-notification) target by external_id so OneSignal can
 * fan out to ALL of a user's subscribed devices/browsers under one
 * call.
 *
 * Usage:
 *   // App bootstrap (once, near the top of the auth-aware tree):
 *   initOneSignalWeb({ appId: import.meta.env.VITE_ONESIGNAL_APP_ID });
 *
 *   // On sign-in / sign-out:
 *   oneSignalLogin(user.id);
 *   oneSignalLogout();
 *
 * All functions silently no-op when the App ID is unset OR running
 * in a non-browser context (SSR, tests). This is intentional —
 * shipping these calls is safe even before VITE_ONESIGNAL_APP_ID
 * lands in Netlify env vars.
 *
 * Web SDK reference: https://documentation.onesignal.com/docs/web-sdk-reference
 *
 * Service worker requirement: OneSignalSDKWorker.js MUST be served
 * from each app's origin (NOT a CDN; NOT redirected). It's a 1-line
 * importScripts of the OneSignal SW. Both apps' public/ folders
 * carry a copy.
 */

declare global {
  interface Window {
    OneSignal?: OneSignalApi;
    OneSignalDeferred?: Array<(os: OneSignalApi) => void | Promise<void>>;
  }
}

/** Minimal surface area we use. The real SDK has dozens more methods. */
interface OneSignalApi {
  init(opts: {
    appId: string;
    safari_web_id?: string;
    allowLocalhostAsSecureOrigin?: boolean;
    serviceWorkerPath?: string;
    serviceWorkerParam?: { scope: string };
  }): Promise<void>;
  login(externalId: string): Promise<void>;
  logout(): Promise<void>;
  Notifications?: {
    requestPermission?: () => Promise<void>;
    permission?: boolean;
  };
}

let initStarted = false;

function runOneSignalAction(
  action: (OneSignal: OneSignalApi) => Promise<void>,
  warnMessage: string,
): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();

  const run = async (OneSignal: OneSignalApi) => {
    try {
      await action(OneSignal);
    } catch (err) {
      console.warn(warnMessage, err);
    }
  };

  if (window.OneSignal) {
    return run(window.OneSignal);
  }

  if (!initStarted) return Promise.resolve();

  window.OneSignalDeferred = window.OneSignalDeferred ?? [];
  return new Promise((resolve) => {
    // Logout callers depend on this resolving only after the SDK action
    // runs. Fence with a 5s timeout fallback so the promise can't hang
    // forever if the SDK script never loads (offline, blocker
    // extension, CDN flake). The double-resolve guard is intentional.
    let resolved = false;
    const finish = () => {
      if (resolved) return;
      resolved = true;
      resolve();
    };
    window.OneSignalDeferred!.push(async (OneSignal) => {
      await run(OneSignal);
      finish();
    });
    setTimeout(() => {
      if (!resolved) {
        console.warn(
          '[oneSignal] runOneSignalAction timeout — SDK never loaded; resolving anyway',
        );
        finish();
      }
    }, 5000);
  });
}

/**
 * Inject the OneSignal Web SDK script tag (idempotent) and run init
 * via the OneSignalDeferred queue. Safe to call multiple times.
 *
 * When appId is missing, this is a complete no-op — no script
 * injection, no console noise. That keeps dev/preview builds quiet
 * and means production traffic without OneSignal env vars doesn't
 * hit OneSignal at all.
 */
export function initOneSignalWeb(opts: { appId: string | undefined }): void {
  if (initStarted) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const appId = (opts.appId ?? '').trim();
  if (!appId) return;

  initStarted = true;

  // 1. Inject the SDK script tag once.
  if (!document.querySelector('script[data-onesignal-sdk]')) {
    const script = document.createElement('script');
    script.src = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';
    script.defer = true;
    script.setAttribute('data-onesignal-sdk', '');
    document.head.appendChild(script);
  }

  // 2. Queue the init call. OneSignalDeferred fires once the script
  // loads, so this works regardless of order.
  window.OneSignalDeferred = window.OneSignalDeferred ?? [];
  window.OneSignalDeferred.push(async (OneSignal) => {
    try {
      // Wait briefly for the page service worker to be active before
      // init. OneSignal posts messages to /sw.js (we configured
      // serviceWorkerPath below to share with the PWA SW); on a cold
      // load the SDK can fire postMessage before the registration
      // resolves, which logs the noisy
      //   "[WM] No SW registration for postMessage"
      // warning on every fresh session.
      //
      // Two things matter for the wait:
      //   1. navigator.serviceWorker.ready ONLY resolves; it never
      //      rejects. In environments where no SW will ever register
      //      (Fitting dev with PWA disabled, private browsing, older
      //      browsers, HTTP-localhost without allowLocalhostAsSecure-
      //      Origin) it hangs forever — a bare try/catch is useless
      //      because the promise never settles. Bugbot caught this.
      //   2. We want the warning gone in production AND init to still
      //      fire in those no-SW environments.
      // Race ready against a 1500ms timeout. Either the SW becomes
      // active and we proceed (warning suppressed), or the timeout
      // fires and we proceed anyway (no-SW env — OneSignal will
      // surface its own error path if it can't register, which is
      // unchanged from before this gate).
      if (typeof navigator !== 'undefined' && navigator.serviceWorker?.ready) {
        await Promise.race([
          navigator.serviceWorker.ready.then(
            () => undefined,
            () => undefined,
          ),
          new Promise<void>((resolve) => setTimeout(resolve, 1500)),
        ]);
      }
      await OneSignal.init({
        appId,
        // Point OneSignal at the existing /sw.js — Fitting's custom SW
        // (apps/fitting/src/sw.ts) does importScripts on OneSignal's
        // SDK worker, so /sw.js handles both PWA caching AND push.
        // Without this, OneSignal would try to register
        // /OneSignalSDKWorker.js at scope /, fighting the PWA SW for
        // ownership — whoever lost the race silently stopped working.
        serviceWorkerPath: 'sw.js',
        serviceWorkerParam: { scope: '/' },
        // localhost is a "secure origin" for dev. Without this the SDK
        // refuses to register the worker on http://localhost:5173.
        allowLocalhostAsSecureOrigin:
          typeof window !== 'undefined' &&
          (window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1'),
      });
      if (!OneSignal.Notifications?.permission) {
        await OneSignal.Notifications?.requestPermission?.();
      }
    } catch (err) {
      // OneSignal init is best-effort. A failure here means no push;
      // it shouldn't take the app down.
      console.warn('[onesignal] init failed', err);
    }
  });
}

/**
 * Identify the current user to OneSignal so server-side push
 * dispatch (via external_id targeting) routes to their devices.
 * Call on sign-in / on every re-auth.
 *
 * If OneSignal isn't loaded yet, queues the login for after init.
 */
export function oneSignalLogin(externalId: string | null | undefined): void {
  if (!externalId) return;
  void runOneSignalAction(async (OneSignal) => {
    try {
      await OneSignal.login(externalId);
    } catch (err) {
      // 400 "Op failed, pausing: login-user" is common when the Web SDK
      // has no push subscription yet (permission denied / CSS blocked /
      // first visit). Not actionable for the golfer session — keep quiet.
      const msg = err instanceof Error ? err.message : String(err);
      if (/400|Op failed|login-user/i.test(msg)) {
        console.warn('[onesignal] login skipped (no subscription yet)');
        return;
      }
      console.warn('[onesignal] login failed', err);
    }
  }, '[onesignal] login failed');
}

/** Sign-out the OneSignal subscription. Call on app logout. */
export function oneSignalLogout(): Promise<void> {
  return runOneSignalAction(
    (OneSignal) => OneSignal.logout(),
    '[onesignal] logout failed',
  );
}
